import { useState, useEffect } from "react";
import { can } from "@/lib/rbac";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BomImage } from "./BomImage";
import BomFormModal from "./BomFormModal";
import AiBomImportDialog from "./AiBomImportDialog";
import StockAdjustDialog from "./StockAdjustDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Boxes, Plus, Pencil, GitMerge, Warehouse, AlertTriangle, Search, Factory, Archive, ArchiveRestore, Link2, ShoppingCart, Loader2, CalendarClock, Copy, Lock, ExternalLink, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { useTn } from "@/lib/i18n";
import { logger } from "@/lib/logger";
import { fmtQty } from "@/lib/constants";
import { ITEM_TYPES, TYPE_LABEL, TYPE_BADGE, typeLabel } from "@/lib/itemTypes";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import { BomExportButton } from "./BomExportButton";
import { MasterRejectDialog } from "@/components/MasterRejectDialog";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";
import { SortHeader, useSort } from "@/components/ui/SortHeader";

// Per-unit quantity of `materialId` inside one unit of `itemId` (recurses through sub-assemblies).
function materialPerUnit(itemId, materialId, byId, seen = new Set()) {
  const item = byId[itemId];
  if (!item || seen.has(itemId)) return 0;
  const next = new Set(seen).add(itemId);
  let total = 0;
  for (const c of item.components || []) {
    if (c.item_id === materialId) total += Number(c.quantity_per_unit) || 0;
    else total += (Number(c.quantity_per_unit) || 0) * materialPerUnit(c.item_id, materialId, byId, next);
  }
  return total;
}

const OPEN_SO = ["Pending", "Confirmed"];

function WhereUsedDialog({ material, items, salesOrders = [], facility = "H. K. INDUSTRIES", canManage, onCreatePO, onCreateWorkOrder, onViewProduct, onClose }) {
  const [demand, setDemand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [raising, setRaising] = useState(false);

  useEffect(() => {
    if (!material) { setDemand(null); return; }
    let active = true;
    setLoading(true);
    api.get(`/orders/material-demand?item_id=${material.id}&facility=${encodeURIComponent(material.tenant_id || facility)}`)
      .then((d) => { if (active) setDemand(d); })
      .catch(() => { if (active) setDemand(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [material, facility]);

  if (!material) return null;
  const byId = Object.fromEntries(items.map((i) => [i.id, i]));
  const componentIds = new Set();
  items.forEach((b) => (b.components || []).forEach((c) => componentIds.add(c.item_id)));
  const finishedGoods = items.filter((b) => b.item_type === "manufactured" && b.status !== "archived" && !componentIds.has(b.id));
  const rows = finishedGoods
    .map((fg) => ({ fg, qty: materialPerUnit(fg.id, material.id, byId) }))
    .filter((r) => r.qty > 0)
    .sort((a, b) => b.qty - a.qty);

  const isSpare = material.item_type === "mfg_spare";
  // Total demand from open sales orders, exploded through each sold product's recipe.
  const soRows = (salesOrders || [])
    .filter((s) => OPEN_SO.includes(s.status))
    .map((s) => ({ so: s, qty: (s.items || []).reduce((a, it) => a + (Number(it.quantity) || 0) * materialPerUnit(it.item_id, material.id, byId), 0) }))
    .filter((r) => r.qty > 0);
  const soDemand = soRows.reduce((a, r) => a + r.qty, 0);

  const netShort = demand?.net_short || 0;
  const inStock = demand?.in_stock ?? (material.stock_quantity ?? 0);
  const soShort = Math.max(0, Math.ceil(soDemand - inStock));
  const topUp = Math.max(0, Math.ceil((material.min_stock || 0) - inStock)); // restock to reorder level
  const suggestedQty = Math.max(1, Math.ceil(Math.max(netShort, isSpare ? soShort : 0)) || topUp || 1);
  const raisePO = async () => {
    setRaising(true);
    try {
      await onCreatePO({ item_id: material.id, sku: material.sku, name: material.name, unit: material.unit,
        suggested_qty: suggestedQty, supplier: material.supplier, lead_time_days: material.lead_time_days });
      onClose();
    } catch (err) {
      logger.debug("Raise PO failed (already surfaced to user):", err);
    } finally {
      setRaising(false);
    }
  };
  const createWO = async () => {
    setRaising(true);
    try {
      await onCreateWorkOrder({ product_name: material.name, bom_item_id: material.id, target_quantity: suggestedQty, priority: "Medium", due_date: new Date(Date.now() + 2 * 86400000).toISOString() });
      onClose();
    } catch (err) {
      logger.debug("Create Work Order failed (already surfaced to user):", err);
    } finally {
      setRaising(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[88vh] overflow-y-auto" data-testid="where-used-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Where used · {material.name}</DialogTitle>
          <DialogDescription className="text-xs">Finished products whose recipe consumes <b>{material.sku}</b> (including via sub-assemblies), plus demand from open production and sales orders.</DialogDescription>
        </DialogHeader>

        {/* Demand forecast */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="where-used-demand">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <CalendarClock className="h-3 w-3" /> Demand Forecast · Open Orders
          </p>
          {loading ? (
            <p className="text-xs text-slate-400 py-2 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Calculating demand…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div><p className="text-[10px] text-slate-400">Total demand</p><p className="text-sm font-mono-data font-semibold text-slate-800" data-testid="demand-total">{(demand?.total_demand ?? 0).toLocaleString("en-IN")} {material.unit}</p></div>
                <div><p className="text-[10px] text-slate-400">In stock</p><p className="text-sm font-mono-data font-semibold text-slate-800">{(demand?.in_stock ?? 0).toLocaleString("en-IN")} {material.unit}</p></div>
                <div><p className="text-[10px] text-slate-400">Net short</p><p className={`text-sm font-mono-data font-semibold ${netShort > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="demand-net">{netShort > 0 ? `${netShort.toLocaleString("en-IN")} ${material.unit}` : "Covered"}</p></div>
              </div>
              {demand?.short_date && (
                <div className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 flex items-start gap-1.5" data-testid="demand-timeline">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 leading-snug">
                    Runs short around <b>{new Date(demand.short_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b>
                    {demand.latest_order_date && <> · order by <b data-testid="demand-order-by">{new Date(demand.latest_order_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</b> ({demand.lead_time_days}d lead)</>}
                  </p>
                </div>
              )}
              {(demand?.orders || []).length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">No open production orders require this material.</p>
              ) : (
                <>
                  <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto rounded border border-slate-100">
                    {demand.orders.map((o) => (
                      <div key={o.order_code} className="flex items-center justify-between py-1 px-1.5" data-testid={`demand-order-${o.order_code}`}>
                        <div className="min-w-0">
                          <p className="text-[11px] font-medium text-slate-700 truncate">{o.product_name} <span className="text-slate-400 font-mono-data">{o.order_code}</span></p>
                        </div>
                        <span className="text-[11px] font-mono-data text-slate-500 whitespace-nowrap">{fmtQty(o.needed, material.unit)} {material.unit}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 text-right" data-testid="demand-order-count">{demand.orders.length} open order{demand.orders.length > 1 ? "s" : ""}{demand.orders.length > 5 ? " · scroll for more" : ""}</p>
                </>
              )}
              {canManage && isSpare && onCreateWorkOrder && (
                <Button size="sm" className="mt-2 w-full h-8 text-[11px] bg-indigo-600 hover:bg-indigo-700" disabled={raising} onClick={createWO} data-testid="where-used-create-wo">
                  <Factory className="h-3 w-3 mr-1" />{raising ? "Creating…" : `Create Work Order · ${fmtQty(suggestedQty, material.unit)} ${material.unit}`}
                </Button>
              )}
              {canManage && !isSpare && onCreatePO && (
                <Button size="sm" className="mt-2 w-full h-8 text-[11px] bg-blue-600 hover:bg-blue-700" disabled={raising} onClick={raisePO} data-testid="where-used-raise-po">
                  <ShoppingCart className="h-3 w-3 mr-1" />{raising ? "Raising…" : `Raise PO for ${material.sku} · ${fmtQty(suggestedQty, material.unit)} ${material.unit}`}
                </Button>
              )}
            </>
          )}
        </div>

        {/* Total demand from open sales orders — highlighted so a shortage is impossible to miss */}
        <div className={`rounded-lg border p-3 ${soShort > 0 ? "border-amber-300 bg-amber-50/70" : "border-indigo-100 bg-indigo-50/50"}`} data-testid="where-used-so-demand">
          <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${soShort > 0 ? "text-amber-700" : "text-indigo-500"}`}>
            {soShort > 0 ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />} Total Demand · Open Sales Orders
          </p>
          <p className="text-sm font-mono-data font-bold text-slate-800" data-testid="so-demand-total">
            {fmtQty(soDemand, material.unit)} {material.unit}
            <span className="text-[10px] font-normal text-slate-400"> across {soRows.length} open sales order{soRows.length === 1 ? "" : "s"}</span>
          </p>
          {soShort > 0 && (
            <p className="text-[11px] text-amber-800 mt-0.5" data-testid="so-demand-short-hint">
              Exceeds current stock ({fmtQty(inStock, material.unit)} {material.unit}) — {isSpare ? "consider starting a Work Order now." : "consider raising a Purchase Order now."}
            </p>
          )}
        </div>

        {/* Where used list */}
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mt-1 flex items-center gap-1">
          <Link2 className="h-3 w-3" /> Used in Finished Products
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-4 text-center" data-testid="where-used-empty">This item isn't used in any finished product's recipe.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(({ fg, qty }) => (
              <div key={fg.id} className="flex items-center justify-between py-2.5 gap-2" data-testid={`where-used-row-${fg.sku}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{fg.name}</p>
                  <p className="text-[11px] text-slate-400 font-mono-data">{fg.sku}</p>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <span className="text-sm font-mono-data font-semibold text-violet-700 whitespace-nowrap">{+qty.toFixed(3)} {material.unit}<span className="text-slate-400 font-normal"> / unit</span></span>
                  {onViewProduct && (
                    <button onClick={() => onViewProduct(fg)} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-0.5" data-testid={`view-bom-${fg.sku}`}>
                      <ExternalLink className="h-3 w-3" /> View Product BOM
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BuildToStockDialog({ item, onClose, onSubmit }) {
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => { setQty(""); setCheck(null); }, [item?.id]);

  useEffect(() => {
    if (!item) return;
    const n = Number(qty);
    if (!(n > 0)) { setCheck(null); return; }
    let ignore = false;
    setChecking(true);
    const t = setTimeout(() => {
      api.get(`/bom/material-check?item_id=${item.id}&quantity=${n}&facility=${encodeURIComponent(item.tenant_id || "H. K. INDUSTRIES")}`)
        .then((r) => { if (!ignore) setCheck(r); })
        .catch(() => { if (!ignore) setCheck(null); })
        .finally(() => { if (!ignore) setChecking(false); });
    }, 350);
    return () => { ignore = true; clearTimeout(t); };
  }, [qty, item]);

  if (!item) return null;
  const short = !!check?.has_shortage;
  const submit = async () => { setSaving(true); try { await onSubmit(item, Number(qty)); onClose(); } finally { setSaving(false); } };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="build-stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Record produced units</DialogTitle>
          <DialogDescription className="text-xs">{item.name} — adds finished units to stock and consumes the recipe materials.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 mt-1">
          <Label className="text-xs">Units Produced ({item.unit})</Label>
          <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="build-qty" autoFocus />
        </div>
        {Number(qty) > 0 && (
          <div className="mt-3 rounded-lg border border-slate-200 p-2 max-h-40 overflow-y-auto" data-testid="build-shortage-preview">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Materials required{checking ? " · checking…" : ""}</p>
            {(check?.materials || []).map((m) => (
              <div key={m.item_id} className="flex items-center justify-between gap-2 text-[11px] py-0.5" data-testid={`build-material-${m.sku}`}>
                <span className={`min-w-0 truncate ${m.shortage > 0 ? "text-red-600 font-medium" : "text-slate-600"}`}>{m.name}</span>
                <span className="font-mono-data text-slate-500 whitespace-nowrap">{m.required} req · {m.in_stock} stock{m.shortage > 0 ? ` · short ${m.shortage}` : ""}</span>
              </div>
            ))}
            {(!check?.materials || check.materials.length === 0) && !checking && (
              <p className="text-[11px] text-slate-400">No recipe materials.</p>
            )}
            {short && <p className="text-[11px] text-red-600 font-medium mt-1" data-testid="build-shortage-warning">Not enough materials in stock to build this quantity.</p>}
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={!(Number(qty) > 0) || saving || checking || short} onClick={submit} data-testid="build-submit">{saving ? "Recording…" : "Add to Stock"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BomRow({ item, itemsById, machinesById = {}, editable, isAdmin = false, onEdit, onDuplicate, onArchive, onAdjustStock, onBuild, onWhereUsed, onApprove, onReject, expected = 0, reservations = [], highlight = false }) {
  const [open, setOpen] = useState(false);
  const buildable = item.item_type === "manufactured" || item.item_type === "mfg_spare";
  const manufactured = buildable; // buildable items show a recipe + Build-to-Stock action
  const showWhereUsed = !manufactured || item.item_type === "mfg_spare"; // raw, spare and manufactured spares
  const tn = useTn();
  const archived = item.status === "archived";
  const pendingApproval = item.approval_status === "Pending Approval";
  const rejected = item.approval_status === "Rejected";
  const stock = item.stock_quantity ?? 0;
  const reserved = item.so_reserved ?? 0;
  const minStock = item.min_stock ?? 0;
  const low = minStock > 0 && stock < minStock;
  const hasDetail = manufactured || ((pendingApproval || rejected) && item.pending_changes && Object.keys(item.pending_changes).length > 0);
  const tint = archived ? "bg-slate-50 opacity-70 hover:bg-slate-100/80" : rejected ? "bg-rose-50/70 border-l-4 border-l-rose-500 hover:bg-rose-100/80" : pendingApproval ? "bg-yellow-50/70 border-l-4 border-l-yellow-500 hover:bg-yellow-100/80" : "";
  return (
    <>
      <tr className={`border-b border-slate-100 transition-colors duration-150 ${tint || "even:bg-slate-50/60 hover:bg-slate-100/80"} ${highlight ? "ring-2 ring-inset ring-indigo-400" : ""}`} data-testid={`bom-card-${item.sku}`}>
        <td className={`${DT.td} w-10 text-center`}>
          {hasDetail ? (
            <button onClick={() => setOpen((x) => !x)} className={DT.iconBtn} data-testid={`bom-expand-${item.sku}`} aria-expanded={open} title={open ? "Hide details" : "Show recipe / change details"}>
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : <span className="inline-block w-7" />}
        </td>
        <td className={`${DT.td} w-10 text-center`}><BomImage src={item.image_url} alt={item.name} className="h-7 w-7 mx-auto" zoomable /></td>
        <td className={`${DT.td} ${DT.stickyLeft} min-w-[220px]`}>
          <p className="text-xs font-semibold text-slate-800 truncate max-w-[220px]">{tn(item.name)}</p>
          <p className="text-[11px] text-slate-400 font-mono-data">{item.sku} · per {item.unit}</p>
          {(item.updated_at || item.created_at) && (
            <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`bom-version-${item.sku}`}>
              v{item.version || 1} · Updated {new Date(item.updated_at || item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
          {pendingApproval && (
            <p className="text-[10px] text-purple-500 mt-0.5" data-testid={`bom-pending-${item.sku}`}>Awaiting admin approval{item.created_by ? ` · raised by ${item.created_by}` : ""}</p>
          )}
          {rejected && item.rejection_reason && (
            <p className="text-[10px] text-rose-600 italic mt-0.5" data-testid={`bom-rejection-${item.sku}`}>Rejected: {item.rejection_reason}</p>
          )}
        </td>
        <td className={DT.td}>
          <span className={`inline-block text-[10px] font-mono-data uppercase tracking-wide px-2 py-0.5 rounded ${TYPE_BADGE[item.item_type] || TYPE_BADGE.raw}`}>
            {TYPE_LABEL[item.item_type] || "Raw Material"}
          </span>
        </td>
        <td className={DT.tdRight}>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-mono-data px-2 py-0.5 rounded ${low ? "bg-amber-50 text-amber-700" : (stock > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600")}`}
            data-testid={`bom-stock-${item.sku}`}
          >
            {low ? <AlertTriangle className="h-2.5 w-2.5" /> : <Warehouse className="h-2.5 w-2.5" />}
            {fmtQty(stock, item.unit)} {item.unit} in stock
          </span>
        </td>
        <td className={DT.tdRight}>
          {reserved > 0 ? (
            <HoverCard openDelay={80} closeDelay={80}>
              <HoverCardTrigger asChild>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono-data px-2 py-0.5 rounded bg-violet-50 text-violet-700 cursor-help" data-testid={`bom-reserved-${item.sku}`}>
                  <Lock className="h-2.5 w-2.5" />
                  {fmtQty(reserved, item.unit)} {item.unit} reserved
                </span>
              </HoverCardTrigger>
              <HoverCardContent align="start" className="w-72 p-3" data-testid={`bom-reserved-detail-${item.sku}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Reserved for confirmed orders
                </p>
                {reservations.length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic" data-testid={`bom-reserved-empty-${item.sku}`}>Held for confirmed sales orders (details unavailable).</p>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {reservations.map((r, i) => (
                      <div key={`${r.so_number}-${i}`} className="flex items-center justify-between py-1.5" data-testid={`bom-reserved-so-${item.sku}-${r.so_number}`}>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-800 font-mono-data truncate">{r.so_number}</p>
                          <p className="text-[10px] text-slate-400 truncate">{r.customer_name || "Customer"}</p>
                        </div>
                        <span className="text-[11px] font-mono-data font-semibold text-violet-700 whitespace-nowrap">{fmtQty(r.quantity, item.unit)} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                )}
              </HoverCardContent>
            </HoverCard>
          ) : <span className="text-slate-300">—</span>}
        </td>
        <td className={DT.tdRight}>
          {expected > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono-data px-2 py-0.5 rounded bg-indigo-50 text-indigo-700" data-testid={`bom-expected-${item.sku}`}>
              +{fmtQty(expected, item.unit)} {item.unit} expected
            </span>
          ) : <span className="text-slate-300">—</span>}
        </td>
        <td className={`${DT.td} text-center`}>
          <div className="inline-flex items-center gap-1.5 flex-wrap justify-center">
            {archived && (
              <span className="inline-block text-[10px] font-mono-data uppercase tracking-wide px-2 py-0.5 rounded bg-slate-200 text-slate-500" data-testid={`bom-archived-${item.sku}`}>Archived</span>
            )}
            {pendingApproval && (
              <span className="inline-block text-[10px] font-mono-data px-2 py-0.5 rounded bg-purple-50 text-purple-700" data-testid={`bom-status-${item.sku}`}>Pending Approval</span>
            )}
            {rejected && (
              <span className="inline-block text-[10px] font-mono-data px-2 py-0.5 rounded bg-rose-50 text-rose-700" data-testid={`bom-status-${item.sku}`}>Rejected</span>
            )}
            {low && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-data px-2 py-0.5 rounded bg-amber-500 text-white" data-testid={`bom-low-stock-${item.sku}`}>
                LOW · reorder at {fmtQty(minStock, item.unit)}
              </span>
            )}
            {!low && minStock > 0 && !archived && (
              <span className="text-[10px] font-mono-data text-slate-400">reorder at {fmtQty(minStock, item.unit)}</span>
            )}
            {!manufactured && (item.lead_time_days ?? 0) > 0 && (
              <span className="text-[10px] font-mono-data text-slate-400" data-testid={`bom-lead-${item.sku}`}>lead {item.lead_time_days}d</span>
            )}
          </div>
        </td>
        <td className={`${DT.td} ${DT.stickyRight}`}>
          <div className={DT.actionsCell}>
            {isAdmin && editable && pendingApproval && (
              <>
                <button onClick={() => onApprove(item)} className="h-7 px-2 text-[11px] font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" title="Approve this item — makes it Active and selectable" data-testid={`approve-bom-${item.sku}`}>Approve</button>
                <button onClick={() => onReject(item)} className="h-7 px-2 text-[11px] font-medium rounded border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors" title="Reject with a comment — sends it back to the planner" data-testid={`reject-bom-${item.sku}`}>Reject</button>
              </>
            )}
            {editable && (archived ? (
              <button onClick={() => onArchive(item, false)} className={`${DT.iconBtn} hover:text-emerald-600`} title="Restore item" data-testid={`restore-bom-${item.sku}`}>
                <ArchiveRestore className="h-3.5 w-3.5" />
              </button>
            ) : (
              <>
                {manufactured && (
                  <button onClick={() => onBuild(item)} className={`${DT.iconBtn} hover:text-blue-600`} title="Production — record produced units to stock" data-testid={`build-${item.sku}`}>
                    <Factory className="h-3.5 w-3.5" />
                  </button>
                )}
                {showWhereUsed && (
                  <button onClick={() => onWhereUsed(item)} className={`${DT.iconBtn} hover:text-violet-600`} title="Where used" data-testid={`where-used-${item.sku}`}>
                    <Link2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={() => onAdjustStock(item)} className={`${DT.iconBtn} hover:text-emerald-600`} title="Adjust stock" data-testid={`adjust-stock-${item.sku}`}>
                  <Warehouse className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onDuplicate(item)} className={`${DT.iconBtn} hover:text-violet-600`} title="Duplicate as a new item" data-testid={`duplicate-bom-${item.sku}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onEdit(item)} className={`${DT.iconBtn} hover:text-blue-600`} title="Edit item" data-testid={`edit-bom-${item.sku}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onArchive(item, true)} className={`${DT.iconBtn} hover:text-amber-600`} title="Archive item (hide from new dropdowns)" data-testid={`archive-bom-${item.sku}`}>
                  <Archive className="h-3.5 w-3.5" />
                </button>
              </>
            ))}
          </div>
        </td>
      </tr>
      {open && hasDetail && (
        <tr className="bg-slate-50/70 border-b border-slate-100">
          <td colSpan={9} className="px-6 py-3">
            <div className="space-y-3">
              {(pendingApproval || rejected) && item.pending_changes && Object.keys(item.pending_changes).length > 0 && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-2.5" data-testid={`bom-changes-${item.sku}`}>
                  <p className="text-[10px] uppercase tracking-wide text-yellow-700 font-semibold mb-1">Changed Fields · Vs Active Version</p>
                  {Object.entries(item.pending_changes).map(([f, ch]) => (
                    <div key={f} className="flex items-center justify-between gap-2 text-[11px] py-0.5" data-testid={`bom-change-${item.sku}-${f}`}>
                      <span className="text-slate-500 font-medium">{CHANGE_LABELS[f] || f}</span>
                      <span className="font-mono-data text-right">
                        <span className="text-slate-400 line-through">{fmtChangeVal(f, ch.old, machinesById)}</span>
                        <span className="text-slate-400 mx-1">→</span>
                        <span className="bg-yellow-200 text-yellow-900 px-1 rounded font-semibold">{fmtChangeVal(f, ch.new, machinesById)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {manufactured && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5 flex items-center gap-1">
                    <GitMerge className="h-3 w-3" /> Recipe ({item.components.length} components)
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {item.components.map((c) => {
                      const comp = itemsById[c.item_id];
                      const nested = comp?.item_type === "manufactured";
                      return (
                        <div key={c.item_id} className="flex items-center justify-between text-[11px]">
                          <span className={`truncate ${nested ? "text-blue-700 font-semibold" : "text-slate-600"}`}>
                            {comp?.name || "?"} {nested && "↳ nested"}
                          </span>
                          <span className="font-mono-data text-slate-500 whitespace-nowrap">×{fmtQty(c.quantity_per_unit, comp?.unit)} {comp?.unit || ""}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const TYPE_FILTERS = [{ key: "all", label: "All" }, ...ITEM_TYPES.map((t) => ({ key: t.value, label: t.label }))];

const FILTER_EMPTY = {
  raw: "raw materials", spare: "brought-out items", mfg_spare: "mfg. spares",
  manufactured: "manufactured items", archived: "archived items", rejected: "items sent back for fixes",
};

// Audit highlight: human labels + value formatting for the planner-edit diff on approval cards.
const CHANGE_LABELS = {
  name: "Name", sku: "SKU", item_type: "Type", unit: "Unit", image_url: "Image",
  components: "Recipe", stages: "Stages", stock_quantity: "Stock", min_stock: "Reorder Level",
  supplier: "Preferred Supplier", category: "Category", lead_time_days: "Lead Time (days)",
  pack_size: "Pack Size", moq: "MOQ", line_id: "Production Line",
};

function fmtChangeVal(field, v, machinesById) {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "image_url") return "set";
  if (Array.isArray(v)) return `${v.length} ${field === "stages" ? "stage(s)" : "component(s)"}`;
  if (field === "line_id") return machinesById?.[v]?.name || machinesById?.[v]?.line_code || "—";
  if (field === "item_type") return typeLabel(v);
  return String(v);
}

export default function BomPanel({ items, role, incoming = {}, suppliers = [], machines = [], facility = "H. K. INDUSTRIES", salesOrders = [], onSave, onArchive, onAdjustStock, onProduce, onCreatePO, onCreateWorkOrder, onApprove, onReject, onImported }) {
  const [modal, setModal] = useState({ open: false, item: null, template: null });
  const [aiOpen, setAiOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [stockItem, setStockItem] = useState(null);
  const [buildItem, setBuildItem] = useState(null);
  const [whereUsed, setWhereUsed] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [highlightSku, setHighlightSku] = useState(null);
  const editable = can.manageOrders(role);
  const isAdmin = role === "admin";
  const itemsById = Object.fromEntries(items.map((i) => [i.id, i]));
  const machinesById = Object.fromEntries((machines || []).map((m) => [m.id, m]));
  // Which confirmed sales orders hold each item's reserved finished-goods stock (for the badge hover).
  const reservationsByItem = {};
  (salesOrders || []).filter((s) => s.status === "Confirmed").forEach((s) => {
    (s.items || []).forEach((it) => {
      (reservationsByItem[it.item_id] ||= []).push({ so_number: s.so_number, customer_name: s.customer_name, quantity: Number(it.quantity || 0), unit: it.unit });
    });
  });
  const archivedCount = items.filter((i) => i.status === "archived").length;
  const rejectedCount = items.filter((i) => i.approval_status === "Rejected").length;
  const q = search.trim().toLowerCase();
  const matchSearch = (i) => !q || i.name.toLowerCase().includes(q) || (i.sku || "").toLowerCase().includes(q);
  const visibleItems = items.filter((i) => {
    if (!matchSearch(i)) return false;
    if (typeFilter === "archived") return i.status === "archived";
    if (typeFilter === "rejected") return i.approval_status === "Rejected";
    if (i.status === "archived") return false; // strictly hide archived under All / Raw / Manufactured
    return typeFilter === "all" || i.item_type === typeFilter;
  });
  const { sort, toggle, apply } = useSort();
  const { pageItems, pagerProps } = usePager(apply(visibleItems, {
    name: (i) => i.name, type: (i) => i.item_type, stock: (i) => i.stock_quantity ?? 0, reserved: (i) => i.so_reserved ?? 0,
  }));
  const countFor = (key) => {
    if (key === "archived") return archivedCount;
    if (key === "rejected") return rejectedCount;
    const active = items.filter((i) => i.status !== "archived");
    return key === "all" ? active.length : active.filter((i) => i.item_type === key).length;
  };
  // "View Product BOM" from the Where-Used dialog: reveal + flash the product's card.
  const viewProductBom = (fg) => {
    setWhereUsed(null);
    setSearch("");
    setTypeFilter(fg.item_type === "mfg_spare" ? "mfg_spare" : "manufactured");
    setHighlightSku(fg.sku);
  };
  useEffect(() => {
    if (!highlightSku) return;
    document.querySelector(`[data-testid="bom-card-${highlightSku}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightSku(null), 3500);
    return () => clearTimeout(t);
  }, [highlightSku]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="bom-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Boxes className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-display text-base font-semibold text-slate-900">Bill of Materials</h3>
            <p className="text-xs text-slate-400">Materials catalog, nested recipes & stock levels · stock auto-deducts as production is logged</p>
          </div>
        </div>
        {editable && (
          <div className="flex items-center gap-1.5">
            {role === "admin" && <BomExportButton facility={facility} scope={typeFilter} count={typeFilter === "archived" ? archivedCount : countFor(typeFilter)} />}
            {role === "admin" && <ImportExportButtons entity="bom" label="items" facility={facility} onImported={onImported} />}
            {role === "admin" && (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAiOpen(true)} data-testid="btn-ai-bom-import">
                <Sparkles className="h-3 w-3 mr-1" /> AI Import
              </Button>
            )}
            <Button size="sm" onClick={() => setModal({ open: true, item: null })} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-bom-item">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mb-3 relative max-w-sm" data-testid="bom-search-wrap">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search materials by name or SKU…" className="pl-9 h-9" data-testid="bom-search-input" />
        </div>
      )}

      {items.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4" data-testid="bom-type-filter">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setTypeFilter(f.key)}
              data-testid={`bom-filter-${f.key}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                typeFilter === f.key
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700"
              }`}
            >
              {f.label} <span className={typeFilter === f.key ? "text-blue-100" : "text-slate-400"}>({countFor(f.key)})</span>
            </button>
          ))}
          {archivedCount > 0 && (
            <button
              onClick={() => setTypeFilter("archived")}
              data-testid="bom-filter-archived"
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ml-1 ${
                typeFilter === "archived"
                  ? "bg-slate-700 border-slate-700 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              Archived <span className={typeFilter === "archived" ? "text-slate-300" : "text-slate-400"}>({archivedCount})</span>
            </button>
          )}
          {rejectedCount > 0 && (
            <button
              onClick={() => setTypeFilter("rejected")}
              data-testid="bom-filter-rejected"
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ml-1 ${
                typeFilter === "rejected"
                  ? "bg-rose-600 border-rose-600 text-white"
                  : "bg-white border-rose-200 text-rose-600 hover:border-rose-400"
              }`}
            >
              Rejected <span className={typeFilter === "rejected" ? "text-rose-200" : "text-rose-400"}>({rejectedCount})</span>
            </button>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-6 text-center" data-testid="bom-empty-state">No BOM items in this facility yet.</p>
      ) : visibleItems.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-6 text-center" data-testid="bom-filter-empty">No {FILTER_EMPTY[typeFilter] || "items"} in this facility.</p>
      ) : (
        <div className={DT.wrap} data-testid="bom-table">
          <table className={`${DT.table} min-w-[1100px]`}>
            <thead className={DT.thead}>
              <tr className={DT.headerRow}>
                <th className={`${DT.th} w-10`}></th>
                <th className={`${DT.th} w-10 text-center`}>Img</th>
                <SortHeader label="Item / SKU" k="name" sort={sort} onSort={toggle} cls={`${DT.th} ${DT.stickyLeft}`} />
                <SortHeader label="Type" k="type" sort={sort} onSort={toggle} cls={DT.th} />
                <SortHeader label="Stock" k="stock" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                <SortHeader label="Reserved" k="reserved" sort={sort} onSort={toggle} align="right" cls={DT.thRight} />
                <th className={DT.thRight}>Expected</th>
                <th className={DT.thCenter}>Status / Reorder</th>
                <th className={`${DT.thRight} ${DT.stickyRight}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => (
                <BomRow
                  key={item.id}
                  item={item}
                  itemsById={itemsById}
                  editable={editable}
                  isAdmin={isAdmin}
                  onApprove={onApprove}
                  onReject={setRejecting}
                  machinesById={machinesById}
                  onEdit={(i) => setModal({ open: true, item: i, template: null })}
                  onDuplicate={(i) => setModal({ open: true, item: null, template: i })}
                  onArchive={onArchive}
                  onAdjustStock={setStockItem}
                  onBuild={setBuildItem}
                  onWhereUsed={setWhereUsed}
                  expected={incoming[item.id] || 0}
                  reservations={reservationsByItem[item.id] || []}
                  highlight={highlightSku === item.sku}
                />
              ))}
            </tbody>
          </table>
          <ListPagination {...pagerProps} testid="bom-pagination" />
        </div>
      )}

      <BuildToStockDialog item={buildItem} onClose={() => setBuildItem(null)} onSubmit={onProduce} />
      <WhereUsedDialog material={whereUsed} items={items} salesOrders={salesOrders} facility={facility} canManage={editable} onCreatePO={onCreatePO} onCreateWorkOrder={onCreateWorkOrder} onViewProduct={viewProductBom} onClose={() => setWhereUsed(null)} />
      <StockAdjustDialog item={stockItem} onClose={() => setStockItem(null)} onSubmit={onAdjustStock} />
      {rejecting && (
        <MasterRejectDialog
          title={`Reject ${rejecting.name}`}
          description="Send this BOM item back to the planner. A rejection comment is required so they know what to fix — they can edit and resubmit it."
          onClose={() => setRejecting(null)}
          onConfirm={(reason) => onReject(rejecting, reason)}
        />
      )}

      {aiOpen && <AiBomImportDialog onClose={() => setAiOpen(false)} facility={facility} onImported={onImported} />}
      <BomFormModal
        open={modal.open}
        item={modal.item}
        template={modal.template}
        items={items}
        suppliers={suppliers}
        machines={machines}
        onOpenChange={(v) => setModal((s) => ({ ...s, open: v }))}
        onSubmit={onSave}
      />
    </div>
  );
}
